/**
 * Shared calendar-date + date-window primitives for API query schemas.
 *
 * Two rules every windowed read endpoint needs, in one place:
 *
 *  1. A date must be a REAL calendar day. `2026-02-31` passes a yyyy-MM-dd
 *     regex and then blows up as a Postgres cast error — a 500 for what is
 *     plainly a bad request.
 *  2. A window must be BOUNDED. Where the underlying RPC scaffolds rows for
 *     the requested span (generate_series over months, say), cost scales with
 *     what the caller asks for rather than with the org's real data, so an
 *     authenticated caller can force a six-figure-row response by hand. Where
 *     it doesn't scaffold, a cap is cheap defence-in-depth against a later
 *     change that introduces one.
 *
 * Pick `maxMonths` from the widest window the UI legitimately asks for, with
 * headroom — the cap is a backstop against pathological input, not a product
 * constraint, and rejecting a range a user can actually select is worse than
 * the DoS it prevents.
 */
import { z } from "zod";
import { isoDateSchema } from "./filterDocumentPrimitives.js";
import { parseLocalStamp, parseLocalTime } from "../format/date.js";
/** yyyy-MM-dd that also names a real calendar day (2026-02-31 is rejected). */
export const calendarDateSchema = isoDateSchema.refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day);
}, "not a real calendar date");
/** Whole calendar months spanned by two yyyy-MM-dd dates, inclusive. */
export function monthSpan(from, to) {
    const [fy, fm] = from.split("-").map(Number);
    const [ty, tm] = to.split("-").map(Number);
    return (ty - fy) * 12 + (tm - fm) + 1;
}
/**
 * "YYYY-MM" keys for every calendar month from `start` to `end` inclusive
 * (both yyyy-MM-dd, or yyyy-MM). Empty when `end` is in an earlier month
 * than `start`.
 */
export function monthKeysBetween(start, end) {
    const [sy, sm] = start.split("-").map(Number);
    const [ey, em] = end.split("-").map(Number);
    const keys = [];
    for (let y = sy, m = sm; y < ey || (y === ey && m <= em);) {
        keys.push(`${y}-${String(m).padStart(2, "0")}`);
        m += 1;
        if (m > 12) {
            m = 1;
            y += 1;
        }
    }
    return keys;
}
/**
 * An inclusive `{ from, to }` query window: both bounds required, both real
 * calendar days, correctly ordered, and no wider than `maxMonths` whole
 * calendar months.
 */
export function makeDateWindowSchema(maxMonths) {
    return z
        .object({ from: calendarDateSchema, to: calendarDateSchema })
        .refine((range) => range.from <= range.to, { message: "from must not be after to" })
        .refine((range) => monthSpan(range.from, range.to) <= maxMonths, {
        message: `range must not exceed ${maxMonths} months`,
    });
}
/**
 * A venue wall-clock time, `HH:mm` (00:00–23:59). No seconds, no zone — see
 * the wall-clock section of `format/date.ts`.
 */
export const localTimeSchema = z
    .string()
    .regex(/^\d{2}:\d{2}$/, "expected HH:mm")
    .refine((value) => parseLocalTime(value) !== null, "not a valid time of day");
/**
 * A venue wall-clock stamp, `YYYY-MM-DDTHH:mm`: no seconds and no offset (a
 * `Z` or `+02:00` suffix is rejected — this is not an instant). The date part
 * must be a real calendar day, as `calendarDateSchema` requires.
 */
export const localStampSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "expected YYYY-MM-DDTHH:mm")
    .refine((value) => parseLocalStamp(value) !== null, "not a real calendar date and time");
//# sourceMappingURL=dateWindow.js.map